import React from "react";
import Select from "react-select";
import { Formik } from "formik";
import { Col, Form, Row } from "react-bootstrap";
import Feedback from "react-bootstrap/Feedback";
import { MenuItem } from "../../../../../components/@types";
import { CUSTOM_SELECT_CLASSNAMES, TEST_ID_KEYS } from "../consts";
import { CustomInput } from "../files/components/CustomInput";
import { CustomOption } from "../files/components/CustomOption";
import { CustomDropdownIndicator } from "../files/components/CustomDropdownIndicator";
import { CustomClassificationMultiValueContainer } from "../files/components/CustomMultiValueContainer";
import { CustomNoOptionsMessage } from "../files/components/CustomNoOptionMessage";
import QuickSearchButtonsGroup from "../components/QuickSearchButtonsGroup";
import { getResourceTypeMenuItems } from "../menuItems";
import ClassificationSelect from "../components/ClassificationSelect";
import TimeRangeSelect from "../components/TimeRangeSelect";
import { QSNetworkFormValues } from "./@types/QSNetworkFormValues";
import { INetworkViewProps } from "./@types/NetworkSearchView";
import { INITIAL_NETWORK_FORM_VALUES } from "./initialFormValues";
import { NETWORK_VALIDATION_SCHEMA } from "./validationSchema";
import { convertNetworkFormToQuery } from "./utils";
import { LOCATION } from "../../../consts";

const NetworkView = ({ onSubmit, onClose }: INetworkViewProps) => {
  const handleFormSubmit = (values: QSNetworkFormValues) => {
    const networkQuery = convertNetworkFormToQuery(values);
    onSubmit({
      ...networkQuery,
      location: LOCATION.NETWORK
    });
  };

  return (
    <Formik
      onSubmit={handleFormSubmit}
      initialValues={INITIAL_NETWORK_FORM_VALUES}
      validationSchema={NETWORK_VALIDATION_SCHEMA}
      validateOnChange={false}
    >
      {({
        handleSubmit,
        handleChange,
        handleReset,
        values,
        errors,
        setFieldValue,
        dirty
      }) => (
        <Form
          noValidate
          onSubmit={handleSubmit}
          className={"quick-search__form-container"}
        >
          <div
            className={"flex flex-column"}
            style={{ height: "100%", justifyContent: "space-between" }}
          >
            <div className={"flex flex-column flex-gap-4"}>
              <Form.Group>
                <Form.Label className={"form-text--important"}>
                  URL, Domain, IP:
                </Form.Label>
                <Form.Control
                  as={"textarea"}
                  placeholder={
                    "Enter URLs, Domains or IP Addresses separated by whitespace, semicolon, comma, tab or new line."
                  }
                  name={"urlDomainIP"}
                  onChange={handleChange}
                  value={values.urlDomainIP}
                  rows={10}
                  isInvalid={!!errors.urlDomainIP}
                />
                <Feedback type="invalid" style={{ display: "block" }}>
                  {errors.urlDomainIP}
                </Feedback>
              </Form.Group>

              <Row>
                <Col xs={2}>
                  <ClassificationSelect
                    isMulti={false}
                    value={values.classification}
                    onChange={value =>
                      setFieldValue(
                        "classification",
                        value ?? INITIAL_NETWORK_FORM_VALUES.classification
                      )
                    }
                  />
                </Col>

                <Col xs={2}>
                  <TimeRangeSelect
                    value={values.firstSeen}
                    onChange={value => setFieldValue("firstSeen", value)}
                    title={"First Seen"}
                    errors={!!errors.firstSeen}
                  />
                </Col>

                <Col xs={2}>
                  <Form.Group>
                    <Form.Label className={"form-text--important"}>
                      Resource Type:
                    </Form.Label>
                    <div data-testid={"resource-type-select"}>
                      <Select<MenuItem, false>
                        isClearable
                        options={getResourceTypeMenuItems()}
                        testIdKey={TEST_ID_KEYS.CLASSIFICATION}
                        value={
                          values.resourceType?.value
                            ? values.resourceType
                            : null
                        }
                        onChange={value => setFieldValue("resourceType", value)}
                        closeMenuOnSelect
                        placeholder="URL / Domain / IP"
                        maxMenuHeight={200}
                        menuShouldScrollIntoView={false}
                        openMenuOnClick
                        classNames={CUSTOM_SELECT_CLASSNAMES}
                        components={{
                          Input: CustomInput,
                          Option: CustomOption,
                          DropdownIndicator: CustomDropdownIndicator,
                          MultiValueContainer:
                            CustomClassificationMultiValueContainer,
                          NoOptionsMessage: CustomNoOptionsMessage
                        }}
                      />
                    </div>
                  </Form.Group>
                </Col>
              </Row>
            </div>
            <QuickSearchButtonsGroup
              onSubmit={handleSubmit}
              onCancel={onClose}
              onClear={handleReset}
              searchDisabled={!dirty}
            />
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default NetworkView;
